// src/components/inventory/SuppliersTab.js
// FlowSense Enterprise UI — Supplier Directory & Analytics
// Enhanced: Glass morphism, Framer Motion, animated stats, search/filter, inline actions, AI Supplier Health
//
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • Connected to purchasing: every supplier shows its FlowSense grade (from real deliveries),
//     on-time %, spend, open orders and the lead time learned from delivered orders — with
//     "Apply" to make the inventory re-order points use it; "Raise PO" opens the PO wizard for that
//     supplier.
//   • Performance Analytics no longer goes blank when the AI health view is missing — FlowSense
//     computes the scorecards from purchase orders (aiService first, then FlowSense).
//   • SLA letter: it was a fixed text after a fake 1.8 s "AI" delay, and "Send to Vendor" only showed
//     "Email Sent" (nothing was sent). The letter now comes from the supplier's real record
//     (FlowSense AI rewrites it when the engine is online) and "Send" opens your email program
//     with it — or copy it.
//   • Live (WebSocket purchase_order_update), FlowSense AI bar, quick action "Add Supplier",
//     read-only users do not see add / edit / archive, CSV export escaped, server errors shown.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, useToast,
  useColorModeValue, Spinner, Text, Flex, HStack, VStack, Icon, Heading,
  Badge, Tooltip, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Input, InputGroup, InputLeftElement, InputRightElement,
  Tag, TagLabel, TagLeftIcon, Divider, Avatar, AvatarBadge,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
  Skeleton, SkeletonText, Progress, Tabs, TabList, TabPanels, Tab, TabPanel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, AlertDescription, Alert, AlertIcon, Textarea,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, EmailIcon, PhoneIcon, CloseIcon } from '@chakra-ui/icons';
import {
  MdBusiness, MdAdd, MdSearch, MdRefresh, MdMoreVert, MdLocationOn,
  MdVerified, MdFilterList, MdDownload, MdOpenInNew, MdStore,
  MdLocalShipping, MdStar, MdTrendingUp, MdGroup, MdSort, MdAutoGraph, MdGavel, MdWarning
} from 'react-icons/md';
import { FaBuilding, FaShippingFast, FaRobot } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import supplierService from '../../services/supplierService';
import aiService from '../../services/aiService'; // Added AI Service
import SupplierFormModal from './SupplierFormModal';
// --- UPDATE (Procurement MOW+ v1)
import { MdContentCopy, MdShoppingCart, MdAutoAwesome } from 'react-icons/md';
import purchaseOrderService, { poErrorMessage } from '../../services/purchaseOrderService';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { negotiationDraft, gradeMeta, usd, answerProcurementLocally, buildProcurementContext, explainProcurementForMinistry } from './procurementAiIntel';
import { downloadCsv, stamp } from '../../utils/inventoryCsv';
const leadOf = (s) => { const v = s?.average_lead_time_days ?? s?.lead_time_days ?? s?.lead_time; return v === null || v === undefined || v === '' ? null : Number(v); };
// --- END UPDATE

// ─── Motion primitives ────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr   = motion(Tr);

// ─── Animation variants ───────────────────────────────────────
const fadeUp  = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

// ─── Stat card ────────────────────────────────────────────────
const FlowStatCard = ({ label, value, sub, color, icon: IconComp, isLoading }) => {
  const bg     = useColorModeValue('white',    'gray.800');
  const border = useColorModeValue('gray.100', 'gray.700');
  const muted  = useColorModeValue('gray.500', 'gray.400');
  const glowMap = {
    purple: 'rgba(128,90,213,0.18)',
    blue:   'rgba(49,130,206,0.18)',
    teal:   'rgba(49,151,149,0.18)',
    green:  'rgba(56,161,105,0.18)',
  };
  return (
    <MotionBox variants={fadeUp} whileHover={{ y: -4, boxShadow: `0 16px 40px ${glowMap[color] || 'rgba(0,0,0,0.12)'}` }}
               transition={{ duration: 0.2 }}>
      <Box bg={bg} borderRadius="2xl" border="1px solid" borderColor={border}
           borderTop="3px solid" borderTopColor={`${color}.400`}
           boxShadow={`0 4px 20px ${glowMap[color] || 'rgba(0,0,0,0.06)'}`}
           p={5} position="relative" overflow="hidden">
        <Box position="absolute" top="-24px" right="-24px" w="90px" h="90px"
             borderRadius="full" bg={`${color}.400`} opacity={0.07} filter="blur(24px)" />
        <Stat position="relative" zIndex={1}>
          <Flex justify="space-between" align="flex-start">
            <Box>
              <StatLabel color={muted} fontWeight="700" fontSize="xs" letterSpacing="wider" mb={2}>
                {label.toUpperCase()}
              </StatLabel>
              {isLoading
                ? <Skeleton height="36px" width="64px" borderRadius="lg" />
                : <StatNumber fontSize="3xl" fontWeight="900" color={`${color}.500`}>{value}</StatNumber>
              }
              {sub && <StatHelpText mt={1} fontSize="xs" color={muted}>{sub}</StatHelpText>}
            </Box>
            <MotionBox
              animate={{ scale: [1, 1.07, 1], opacity: [0.8, 1, 0.8] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              bg={`${color}.50`} p={3} borderRadius="xl"
            >
              <Icon as={IconComp} color={`${color}.500`} boxSize={6} />
            </MotionBox>
          </Flex>
        </Stat>
      </Box>
    </MotionBox>
  );
};

// ─── Supplier avatar with initials ───────────────────────────
const SupplierAvatar = ({ name, isActive }) => {
  const colors = ['purple', 'blue', 'teal', 'green', 'cyan', 'orange'];
  const color  = colors[(name?.charCodeAt(0) || 0) % colors.length];
  const initials = (name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <Avatar size="sm" name={name} bg={`${color}.500`} color="white"
            fontSize="xs" fontWeight="800" getInitials={() => initials}>
      <AvatarBadge boxSize="1.15em"
                   bg={isActive !== false ? 'green.400' : 'gray.300'}
                   border="2px solid white" />
    </Avatar>
  );
};

// ─── Lead time badge ─────────────────────────────────────────
const LeadTimeBadge = ({ days }) => {
  const d = parseInt(days, 10) || 0;
  const scheme = d <= 3 ? 'green' : d <= 7 ? 'blue' : d <= 14 ? 'yellow' : 'red';
  return (
    <Tag size="sm" colorScheme={scheme} borderRadius="full" variant="subtle">
      <TagLeftIcon as={MdLocalShipping} boxSize={3} />
      <TagLabel fontSize="xs" fontWeight="700">{d}d lead</TagLabel>
    </Tag>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const SuppliersTab = ({ onRaisePO = null, canManage = true, command = null }) => { // --- UPDATE: + onRaisePO, canManage, command
  const [suppliers,         setSuppliers]         = useState([]);
  const [healthData,        setHealthData]        = useState([]); // Added AI Health State
  const [isLoading,         setIsLoading]         = useState(false);
  const [isHealthLoading,   setIsHealthLoading]   = useState(false); // Added AI Health Loading
  const [isModalOpen,       setIsModalOpen]       = useState(false);
  const [selectedSupplier,  setSelectedSupplier]  = useState(null);
  const [deleteTarget,      setDeleteTarget]      = useState(null);
  const [isDeleting,        setIsDeleting]        = useState(false);
  const [searchQuery,       setSearchQuery]       = useState('');
  const [sortBy,            setSortBy]            = useState('name');
  const [sortDir,           setSortDir]           = useState('asc');
  const [isRefreshing,      setIsRefreshing]      = useState(false);

  // AI Draft Modal State
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const [activeHealthSupplier, setActiveHealthSupplier] = useState(null);
  // --- UPDATE (Procurement MOW+ v1)
  const [procIntel, setProcIntel] = useState(null);
  const [healthSource, setHealthSource] = useState(null);
  const warnBg = useColorModeValue('orange.50', 'rgba(221,107,32,0.15)');
  const warnText = useColorModeValue('orange.700', 'orange.200');
  // --- END UPDATE

  const toast     = useToast({ position: 'top-right' });
  const cancelRef = useRef();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // ── Theme tokens ─────────────────────────────────────────
  const bg          = useColorModeValue('white',     'gray.800');
  const pageBg      = useColorModeValue('gray.50',   'gray.900');
  const border      = useColorModeValue('gray.100',  'gray.700');
  const headerBg    = useColorModeValue('gray.50',   'gray.900');
  const hoverBg     = useColorModeValue('purple.50', 'whiteAlpha.50');
  const muted       = useColorModeValue('gray.500',  'gray.400');
  const textColor   = useColorModeValue('gray.800',  'white');
  const glassStyle  = {
    backdropFilter: 'blur(12px)',
    background: useColorModeValue('rgba(255,255,255,0.9)', 'rgba(26,32,44,0.9)'),
  };

  // ── Fetch Suppliers & AI Health Data ───────────────────────
  const fetchSuppliers = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const data = await supplierService.getAllSuppliers();
      setSuppliers(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []); // --- UPDATE: { data } too
    } catch {
      toast({ title: 'Sync Error', description: 'Could not load supplier directory.', status: 'error', duration: 4000 });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [toast]);

  const fetchSupplierHealth = async () => {
    setIsHealthLoading(true);
    // --- UPDATE: FlowSense scorecards (real purchase orders) — used for the directory and as the fallback
    let intel = null;
    try { intel = await purchaseOrderService.getIntel(); setProcIntel(intel); live.markUpdated(); } catch (_) { /* purchasing not deployed */ }
    // --- END UPDATE
    try {
      const res = await aiService.getSupplierHealth();
      if (res && res.success && Array.isArray(res.data) && res.data.length) { // --- UPDATE: empty → FlowSense
        setHealthData(res.data);
        setHealthSource('ai'); // --- UPDATE
      } else throw new Error('empty'); // --- UPDATE
    } catch (error) {
      // --- UPDATE: AI health view missing / offline → FlowSense scorecards for suppliers with orders
      const fs = (intel?.suppliers || []).filter((x) => x.total_orders > 0);
      setHealthData(fs); setHealthSource(fs.length ? 'flowsense' : null);
      if (!fs.length) console.error('Failed to load supplier health analytics', error);
      // --- END UPDATE
    } finally {
      setIsHealthLoading(false);
    }
  };

  // --- UPDATE (Procurement MOW+ v1): live + quick actions + per-supplier record
  const live = useInventoryLive(() => { fetchSupplierHealth(); }, { events: ['purchase_order_update'] });
  useEffect(() => { if (command?.type === 'addSupplier' && canManage) { setSelectedSupplier(null); setIsModalOpen(true); } }, [command]); // eslint-disable-line react-hooks/exhaustive-deps
  const scoreById = useMemo(() => new Map((procIntel?.suppliers || []).map((x) => [String(x.supplier_id), x])), [procIntel]);
  const applyLeadTime = async (sc) => {
    try { const r = await purchaseOrderService.applyLeadTime(sc.supplier_id, sc.learned_lead_time_days); toast({ title: `${sc.name}: lead time ${r.lead_time_days} days`, description: 'FlowSense re-order points use the real lead time now.', status: 'success' }); fetchSuppliers(true); fetchSupplierHealth(); }
    catch (e) { toast({ title: 'Lead time not changed', description: poErrorMessage(e), status: 'error' }); }
  };
  // --- END UPDATE

  // FIX: Isolated useEffect with disabled exhaustive-deps to prevent the Infinity Load Glitch
  useEffect(() => { 
    fetchSuppliers(); 
    fetchSupplierHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Stats ─────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:         suppliers.length,
    activeRegions: [...new Set(suppliers.map(s => s.address?.split(',').pop()?.trim()).filter(Boolean))].length,
    // --- UPDATE: lead_time_days too; average of suppliers that have one ("—" when none)
    avgLeadTime:   suppliers.filter((x) => leadOf(x) !== null).length
      ? Math.round(suppliers.filter((x) => leadOf(x) !== null).reduce((s, x) => s + leadOf(x), 0) / suppliers.filter((x) => leadOf(x) !== null).length)
      : null,
    withContact:   suppliers.filter(s => s.email || s.phone).length,
  }), [suppliers]);

  // ── Filtered + sorted list ────────────────────────────────
  const displayed = useMemo(() => {
    let list = [...suppliers];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.contact_person?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.address?.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const av = a[sortBy] || '';
      const bv = b[sortBy] || '';
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [suppliers, searchQuery, sortBy, sortDir]);

  // ── Delete ─────────────────────────────────────────────────
  const openDelete = (supplier) => { setDeleteTarget(supplier); onDeleteOpen(); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await supplierService.deleteSupplier(deleteTarget.id);
      setSuppliers(p => p.filter(s => s.id !== deleteTarget.id));
      toast({ title: `${deleteTarget.name} archived`, status: 'success', duration: 3000 });
      onDeleteClose();
    } catch (e) {
      toast({ title: 'Archive failed', description: poErrorMessage(e), status: 'error' }); // --- UPDATE: e.g. supplier still on purchase orders
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  // ── Export CSV ────────────────────────────────────────────
  const handleExport = () => {
    // --- UPDATE: escaped CSV (a quote in a name broke the file) + FlowSense columns
    const headers = ['Name', 'Contact', 'Email', 'Phone', 'Address', 'Lead Time', 'Learned Lead Time', 'Grade', 'On-time %', 'Orders', 'Spend'];
    const rows = displayed.map(s => { const sc = scoreById.get(String(s.id)) || {}; return [s.name, s.contact_person, s.email, s.phone, s.address, leadOf(s), sc.learned_lead_time_days, sc.supplier_grade, sc.on_time_delivery_pct, sc.total_orders, sc.total_spend]; });
    downloadCsv(`suppliers-${stamp()}.csv`, headers, rows);
    // --- END UPDATE
    toast({ title: 'Exported to CSV', status: 'success', duration: 2000 });
  };

  const cycleSort = (field) => {
    if (sortBy === field) setSortDir(p => p === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) =>
    sortBy === field
      ? <Icon as={sortDir === 'asc' ? MdTrendingUp : MdSort} boxSize={3} color="purple.400" />
      : null;

  // ── AI Negotiation Draft Handler ──────────────────────────
  const handleDraftNegotiation = async (supplier) => {
    setActiveHealthSupplier(supplier);
    setDraftModalOpen(true);
    setIsDrafting(true);
    // --- UPDATE: letter from the supplier's real record; FlowSense AI rewrites it when online
    const base = negotiationDraft({ ...(scoreById.get(String(supplier.supplier_id)) || {}), ...supplier });
    let text = base;
    try {
      const r = await askFlowSenseAi('Rewrite this supplier performance letter in a firm, professional tone for the Bahamas Ministry of Works. Keep every number exactly as given and add nothing new.', { letter: base, supplier: supplier.name }, 'inventory');
      if (r?.answer && r.answer.length > 80) text = r.answer;
    } catch (_) { /* engine offline — the letter from the record is used */ }
    setDraftContent(text);
    setIsDrafting(false);
    return;
    // --- END UPDATE
    // eslint-disable-next-line no-unreachable
    setTimeout(() => {
      setDraftContent(`Subject: Urgent: Service Level Agreement (SLA) Performance Review - ${supplier.name}

Dear ${supplier.name} Management Team,

I am writing on behalf of the Nassau Ministry of Works regarding your recent vendor performance metrics. 

Our AI-driven procurement analytics system has flagged your account with a "${supplier.supplier_grade}" grade. Over your last ${supplier.total_orders} orders, your on-time delivery rate has fallen to ${supplier.on_time_delivery_pct}%, with an average delay of ${supplier.avg_delay_days} days.

These delays directly impact critical public water infrastructure projects. To maintain our vendor partnership, we require an immediate remediation plan. Furthermore, we would like to schedule a call to discuss applying a late-delivery discount to offset the operational disruptions caused by these delays.

Please advise on your availability this week.

Sincerely,
Senior Procurement Officer
Ministry of Works`);
      setIsDrafting(false);
    }, 1800);
  };

  const getGradeUI = (grade) => {
    switch(grade) {
      case 'A': return { color: 'green', text: 'Excellent' };
      case 'B': return { color: 'blue', text: 'Good' };
      case 'C': return { color: 'orange', text: 'Needs Improvement' };
      case 'F': return { color: 'red', text: 'Failing' };
      default: return { color: 'gray', text: 'Unrated' };
    }
  };

  // ── RENDER ────────────────────────────────────────────────
  return (
    <Box bg={pageBg} minH="60vh" p={{ base: 3, md: 5 }}>

      {/* --- UPDATE (Procurement MOW+ v1): FlowSense AI */}
      <Box mb={6}>
        <FlowSenseInsightBar screen="suppliers" title="Suppliers" data={procIntel}
          headline={procIntel ? [`${(procIntel.suppliers || []).filter((x) => x.supplier_grade).length} graded · ${(procIntel.suppliers || []).filter((x) => ['C', 'F'].includes(x.supplier_grade)).length} need improvement · ${(procIntel.suppliers || []).filter((x) => x.lead_time_gap_pct !== null && Math.abs(x.lead_time_gap_pct) >= 30).length} with a wrong lead time`, 'Grades come from real deliveries: on-time %, delay, cancellations.'] : `${suppliers.length} suppliers`}
          rulesAnswer={(q) => answerProcurementLocally(q, procIntel).answer} ministryText={explainProcurementForMinistry} contextBuilder={() => buildProcurementContext(procIntel)}
          questions={['Which suppliers perform worst?', 'What are the real lead times?', 'Explain purchasing for the Ministry of Works']} />
      </Box>
      {/* --- END UPDATE */}

      {/* ═══ STAT CARDS ═══ */}
      <MotionBox variants={stagger} initial="hidden" animate="show" mb={8}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
          <FlowStatCard label="Active Partners"     value={stats.total}         color="purple" icon={FaBuilding}     sub="Registered suppliers"         isLoading={isLoading} />
          <FlowStatCard label="Distribution Nodes"  value={stats.activeRegions} color="blue"   icon={MdLocationOn}   sub="Unique regions"                 isLoading={isLoading} />
          <FlowStatCard label="Avg Lead Time"       value={stats.avgLeadTime !== null ? `${stats.avgLeadTime}d` : '—'} color="teal" icon={FaShippingFast} sub="Days from order to delivery"  isLoading={isLoading} />{/* --- UPDATE: "—" when unknown */}
          <FlowStatCard label="Contact Coverage"    value={stats.withContact}   color="green"  icon={MdGroup}        sub="Suppliers with contact info"    isLoading={isLoading} />
        </SimpleGrid>
      </MotionBox>

      {/* ═══ ENTERPRISE TABS (Directory vs Analytics) ═══ */}
      <Tabs variant="soft-rounded" colorScheme="purple" isLazy>
        <TabList mb={6}>
          <Tab fontWeight="bold" borderRadius="xl"><Icon as={MdBusiness} mr={2} /> Supplier Directory</Tab>
          <Tab fontWeight="bold" borderRadius="xl"><Icon as={MdAutoGraph} mr={2} /> Performance Analytics</Tab>
        </TabList>

        <TabPanels>
          {/* TAB 1: EXISTING DIRECTORY */}
          <TabPanel px={0} pt={0}>
            {/* HEADER + TOOLBAR */}
            <MotionFlex
              initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              justify="space-between" align="center" mb={5}
              bg={bg} p={5} borderRadius="2xl" boxShadow="sm"
              border="1px solid" borderColor={border}
              flexWrap="wrap" gap={4} {...glassStyle}
            >
              {/* Brand block */}
              <HStack spacing={4}>
                <MotionBox
                  initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 14 }}
                  p={3} bgGradient="linear(to-br, purple.500, blue.500)"
                  borderRadius="xl" boxShadow="0 4px 16px rgba(128,90,213,0.4)"
                >
                  <Icon as={MdBusiness} color="white" boxSize={6} />
                </MotionBox>
                <Box>
                  <Heading size="md" fontWeight="800" letterSpacing="-0.01em">Supplier Directory</Heading>
                  <HStack spacing={2} mt={0.5}>
                    <Badge colorScheme="purple" variant="subtle" borderRadius="full" fontSize="10px">
                      Global Sourcing
                    </Badge>
                    <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="10px">
                      {stats.total} Partners
                    </Badge>
                  </HStack>
                </Box>
              </HStack>

              {/* Toolbar */}
              <HStack spacing={3} flexWrap="wrap">
                <InputGroup size="sm" w={{ base: 'full', sm: '220px' }}>
                  <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                  <Input borderRadius="xl" placeholder="Search suppliers…"
                         value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                  {searchQuery && (
                    <InputRightElement>
                      <IconButton size="xs" variant="ghost" icon={<CloseIcon boxSize={2} />}
                                  onClick={() => setSearchQuery('')} aria-label="Clear" />
                    </InputRightElement>
                  )}
                </InputGroup>

                <Tooltip label="Refresh" hasArrow>
                  <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <IconButton icon={<Icon as={MdRefresh} />} size="sm" variant="ghost"
                                colorScheme="purple" borderRadius="full" aria-label="Refresh"
                                isLoading={isRefreshing} onClick={() => { fetchSuppliers(true); fetchSupplierHealth(); }} />
                  </MotionBox>
                </Tooltip>

                <Tooltip label="Export CSV" hasArrow>
                  <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <IconButton icon={<Icon as={MdDownload} />} size="sm" variant="ghost"
                                colorScheme="blue" borderRadius="full" aria-label="Export"
                                onClick={handleExport} />
                  </MotionBox>
                </Tooltip>

                <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} />{/* --- UPDATE */}
                {canManage && ( /* --- UPDATE */
                <MotionBox whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    leftIcon={<Icon as={MdAdd} />} colorScheme="purple" size="sm"
                    borderRadius="xl" fontWeight="700"
                    bgGradient="linear(to-r, purple.500, blue.500)"
                    _hover={{ bgGradient: 'linear(to-r, purple.600, blue.600)', transform: 'translateY(-1px)' }}
                    boxShadow="0 4px 14px rgba(128,90,213,0.35)"
                    onClick={() => { setSelectedSupplier(null); setIsModalOpen(true); }}
                  >
                    New Partner
                  </Button>
                </MotionBox>
                )}
              </HStack>
            </MotionFlex>

            {/* Filter tags */}
            {searchQuery && (
              <MotionBox initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} mb={4}>
                <HStack>
                  <Tag colorScheme="purple" borderRadius="full" size="sm">
                    <TagLabel>"{searchQuery}" — {displayed.length} results</TagLabel>
                  </Tag>
                  <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => setSearchQuery('')}>
                    Clear
                  </Button>
                </HStack>
              </MotionBox>
            )}

            {/* TABLE */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.45 }}
            >
              <Box bg={bg} borderRadius="2xl" overflow="hidden" boxShadow="xl"
                   border="1px solid" borderColor={border} {...glassStyle}>
                <Box h="3px" bgGradient="linear(to-r, purple.500, blue.400, teal.400)" />

                {isLoading ? (
                  <Box p={6}>
                    <VStack spacing={4}>
                      {[...Array(4)].map((_, i) => (
                        <Box key={i} w="full" p={3} borderRadius="xl" border="1px solid" borderColor={border}>
                          <HStack spacing={4}>
                            <Skeleton borderRadius="full" boxSize="36px" />
                            <SkeletonText noOfLines={2} spacing={2} flex={1} />
                            <Skeleton height="20px" width="80px" borderRadius="full" />
                            <Skeleton height="20px" width="60px" borderRadius="full" />
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                ) : displayed.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" p={24} gap={4}>
                    <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
                      <Box bg="purple.50" p={6} borderRadius="full">
                        <Icon as={MdBusiness} boxSize={14} color="purple.300" />
                      </Box>
                    </MotionBox>
                    <Text fontWeight="800" fontSize="lg" color={textColor}>
                      {suppliers.length === 0 ? 'No Suppliers Yet' : 'No Results Found'}
                    </Text>
                    <Text color={muted} fontSize="sm" textAlign="center" maxW="xs">
                      {suppliers.length === 0
                        ? 'Add your first supplier partner to get started with procurement.'
                        : `No suppliers match "${searchQuery}". Try a different search.`}
                    </Text>
                    {suppliers.length === 0 ? (
                      <Button leftIcon={<Icon as={MdAdd} />} colorScheme="purple" borderRadius="xl" size="sm"
                              onClick={() => { setSelectedSupplier(null); setIsModalOpen(true); }}>
                        Add First Supplier
                      </Button>
                    ) : (
                      <Button variant="outline" colorScheme="purple" size="sm" borderRadius="xl"
                              onClick={() => setSearchQuery('')}>
                        Clear Search
                      </Button>
                    )}
                  </Flex>
                ) : (
                  <Table variant="simple">
                    <Thead bg={headerBg}>
                      <Tr>
                        <Th py={4}>
                          <HStack spacing={1} cursor="pointer" onClick={() => cycleSort('name')}
                                  _hover={{ color: 'purple.500' }}>
                            <Text>Company</Text><SortIcon field="name" />
                          </HStack>
                        </Th>
                        <Th>
                          <HStack spacing={1} cursor="pointer" onClick={() => cycleSort('contact_person')}
                                  _hover={{ color: 'purple.500' }}>
                            <Text>Point of Contact</Text><SortIcon field="contact_person" />
                          </HStack>
                        </Th>
                        <Th>Communication</Th>
                        <Th>Details</Th>
                        <Th textAlign="right">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <AnimatePresence>
                        {displayed.map((supplier, idx) => (
                          <MotionTr
                            key={supplier.id}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 12 }}
                            transition={{ delay: Math.min(idx * 0.05, 0.5) }}
                            _hover={{ bg: hoverBg, cursor: 'default' }}
                          >
                            {/* Company */}
                            <Td>
                              <HStack spacing={3}>
                                <SupplierAvatar name={supplier.name} isActive={supplier.is_active} />
                                <VStack align="start" spacing={0}>
                                  <HStack spacing={1}>
                                    <Text fontWeight="700" fontSize="sm" color={textColor}>{supplier.name}</Text>
                                    {supplier.is_verified && (
                                      <Icon as={MdVerified} color="blue.400" boxSize={4} />
                                    )}
                                  </HStack>
                                  {supplier.address && (
                                    <HStack spacing={1}>
                                      <Icon as={MdLocationOn} boxSize={3} color={muted} />
                                      <Text fontSize="xs" color={muted} noOfLines={1} maxW="160px">
                                        {supplier.address}
                                      </Text>
                                    </HStack>
                                  )}
                                </VStack>
                              </HStack>
                            </Td>

                            {/* Contact */}
                            <Td>
                              {supplier.contact_person ? (
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="600">{supplier.contact_person}</Text>
                                  {supplier.contact_title && (
                                    <Text fontSize="xs" color={muted}>{supplier.contact_title}</Text>
                                  )}
                                </VStack>
                              ) : (
                                <Text fontSize="xs" color={muted} fontStyle="italic">Not assigned</Text>
                              )}
                            </Td>

                            {/* Communication */}
                            <Td>
                              <HStack spacing={2}>
                                {supplier.email && (
                                  <Tooltip label={supplier.email} hasArrow>
                                    <MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
                                      <IconButton
                                        size="sm" icon={<EmailIcon />} variant="ghost"
                                        colorScheme="purple" borderRadius="full"
                                        onClick={() => window.location.href = `mailto:${supplier.email}`}
                                        aria-label={`Email ${supplier.name}`}
                                      />
                                    </MotionBox>
                                  </Tooltip>
                                )}
                                {supplier.phone && (
                                  <Tooltip label={supplier.phone} hasArrow>
                                    <MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
                                      <IconButton
                                        size="sm" icon={<PhoneIcon />} variant="ghost"
                                        colorScheme="blue" borderRadius="full"
                                        onClick={() => window.location.href = `tel:${supplier.phone}`}
                                        aria-label={`Call ${supplier.name}`}
                                      />
                                    </MotionBox>
                                  </Tooltip>
                                )}
                                {!supplier.email && !supplier.phone && (
                                  <Text fontSize="xs" color={muted} fontStyle="italic">No contact</Text>
                                )}
                              </HStack>
                            </Td>

                            {/* Details */}
                            <Td>
                              <VStack align="start" spacing={1}>
                                {leadOf(supplier) !== null && ( /* --- UPDATE: lead_time_days too */
                                  <LeadTimeBadge days={leadOf(supplier)} />
                                )}
                                {/* --- UPDATE (Procurement MOW+ v1): FlowSense record */}
                                {(() => { const sc = scoreById.get(String(supplier.id)); if (!sc) return null; const gm = gradeMeta(sc.supplier_grade); return (
                                  <>
                                    <Tooltip hasArrow label={sc.grade_reason}><HStack spacing={1} cursor="help"><Badge colorScheme={gm.color} fontSize="9px" borderRadius="full">{sc.supplier_grade ? `Grade ${sc.supplier_grade}` : 'Unrated'}</Badge>
                                      {sc.on_time_delivery_pct !== null && <Text fontSize="10px" color={muted}>{sc.on_time_delivery_pct}% on time</Text>}</HStack></Tooltip>
                                    {sc.total_orders > 0 && <Text fontSize="10px" color={muted}>{sc.total_orders} PO · {usd(sc.total_spend)}{sc.open_orders ? ` · ${sc.open_orders} open` : ''}</Text>}
                                    {sc.learned_lead_time_days !== null && <HStack spacing={1}><Text fontSize="10px" color={muted}>real lead {sc.learned_lead_time_days}d</Text>
                                      {canManage && sc.learned_lead_time_days !== leadOf(supplier) && <Button size="xs" variant="link" colorScheme="teal" fontSize="10px" onClick={() => applyLeadTime(sc)} aria-label={`Apply learned lead time ${supplier.name}`}>Apply</Button>}</HStack>}
                                  </>); })()}
                                {/* --- END UPDATE */}
                                {supplier.payment_terms && (
                                  <Badge colorScheme="gray" fontSize="9px" borderRadius="full" variant="subtle">
                                    {supplier.payment_terms}
                                  </Badge>
                                )}
                              </VStack>
                            </Td>

                            {/* Actions */}
                            <Td textAlign="right">
                              <HStack spacing={1} justify="flex-end">
                                {/* --- UPDATE: raise a PO for this supplier */}
                                {onRaisePO && canManage && <Tooltip label="Raise purchase order" hasArrow><IconButton icon={<Icon as={MdShoppingCart} />} size="sm" variant="ghost" colorScheme="green" borderRadius="full" onClick={() => onRaisePO(supplier)} aria-label={`Raise PO ${supplier.name}`} /></Tooltip>}
                                {canManage && (<>
                                <Tooltip label="Edit Partner" hasArrow>
                                  <MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
                                    <IconButton
                                      icon={<EditIcon />} size="sm" variant="ghost"
                                      colorScheme="blue" borderRadius="full"
                                      onClick={() => { setSelectedSupplier(supplier); setIsModalOpen(true); }}
                                      aria-label="Edit"
                                    />
                                  </MotionBox>
                                </Tooltip>
                                <Tooltip label="Archive Partner" hasArrow>
                                  <MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
                                    <IconButton
                                      icon={<DeleteIcon />} size="sm" variant="ghost"
                                      colorScheme="red" borderRadius="full"
                                      onClick={() => openDelete(supplier)}
                                      aria-label="Archive"
                                    />
                                  </MotionBox>
                                </Tooltip>
                                </>)}{/* --- UPDATE: canManage */}
                              </HStack>
                            </Td>
                          </MotionTr>
                        ))}
                      </AnimatePresence>
                    </Tbody>
                  </Table>
                )}

                {/* Table footer */}
                {!isLoading && displayed.length > 0 && (
                  <Flex justify="space-between" align="center" px={5} py={3}
                        borderTop="1px solid" borderColor={border} bg={headerBg}
                        flexWrap="wrap" gap={2}>
                    <Text fontSize="xs" color={muted}>
                      {displayed.length} of {suppliers.length} suppliers
                      {searchQuery ? ` matching "${searchQuery}"` : ''}
                    </Text>
                    <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<Icon as={MdDownload} />}
                            borderRadius="xl" onClick={handleExport}>
                      Export CSV
                    </Button>
                  </Flex>
                )}
              </Box>
            </MotionBox>
          </TabPanel>

          {/* TAB 2: AI SUPPLIER HEALTH DASHBOARD */}
          <TabPanel px={0} pt={0}>
             <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
               {isHealthLoading ? (
                 <Flex direction="column" align="center" justify="center" p={20}>
                   <Spinner size="xl" color="purple.500" thickness="4px" />
                   <Text mt={4} color={muted} fontWeight="bold">Analyzing Supply Chain History...</Text>
                 </Flex>
               ) : healthData.length === 0 ? (
                 <Box bg={bg} p={10} borderRadius="2xl" border="1px solid" borderColor={border} textAlign="center">
                   <Icon as={MdWarning} boxSize={10} color="orange.400" mb={3} />
                   <Heading size="md" mb={2}>No Purchase Data Available</Heading>
                   <Text color={muted}>The AI engine requires historical purchase orders to grade suppliers.</Text>
                 </Box>
               ) : (
                 <>
                 {/* --- UPDATE: where the grades come from */}
                 {healthSource === 'flowsense' && <Alert status="info" borderRadius="xl" mb={4}><AlertIcon as={MdAutoAwesome} /><AlertDescription fontSize="sm">Scorecards computed by FlowSense from your purchase orders (on-time % needs an expected delivery date; grades need 2+ dated deliveries).</AlertDescription></Alert>}
                 <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                   {healthData.map((h, index) => {
                     const gradeData = getGradeUI(h.supplier_grade);
                     const isFailing = h.supplier_grade === 'C' || h.supplier_grade === 'F';

                     return (
                       <MotionBox 
                         key={h.supplier_id}
                         initial={{ opacity: 0, scale: 0.95 }}
                         animate={{ opacity: 1, scale: 1 }}
                         transition={{ delay: index * 0.1 }}
                         bg={bg} p={6} borderRadius="2xl" border="1px solid" borderColor={border}
                         boxShadow="sm" _hover={{ boxShadow: 'lg', borderColor: `${gradeData.color}.300` }}
                       >
                         <Flex justify="space-between" align="start" mb={4}>
                           <VStack align="start" spacing={1}>
                             <Text fontWeight="800" fontSize="lg">{h.name}</Text>
                             <Badge colorScheme={gradeData.color} variant="subtle" borderRadius="full" px={2}>
                               {gradeData.text}
                             </Badge>
                           </VStack>
                           <Box textAlign="center" bg={`${gradeData.color}.50`} p={3} borderRadius="xl" border="1px solid" borderColor={`${gradeData.color}.200`}>
                             <Text fontSize="xs" fontWeight="bold" color={`${gradeData.color}.700`}>AI GRADE</Text>
                             <Text fontSize="4xl" fontWeight="black" color={`${gradeData.color}.500`} lineHeight={1}>
                               {h.supplier_grade}
                             </Text>
                           </Box>
                         </Flex>

                         <Divider mb={4} />

                         <SimpleGrid columns={3} spacing={4} mb={6}>
                           <VStack align="start" spacing={0}>
                             <Text fontSize="xs" color={muted} fontWeight="bold">TOTAL SPEND</Text>
                             <Text fontWeight="black">{usd(h.total_spend)}</Text>{/* --- UPDATE */}
                           </VStack>
                           <VStack align="start" spacing={0}>
                             <Text fontSize="xs" color={muted} fontWeight="bold">ON-TIME %</Text>
                             <Text fontWeight="black" color={h.on_time_delivery_pct < 80 ? 'red.500' : 'green.500'}>
                               {h.on_time_delivery_pct === null || h.on_time_delivery_pct === undefined ? '—' : `${h.on_time_delivery_pct}%`}{/* --- UPDATE: unknown is not 0 % */}
                             </Text>
                           </VStack>
                           <VStack align="start" spacing={0}>
                             <Text fontSize="xs" color={muted} fontWeight="bold">AVG DELAY</Text>
                             <Text fontWeight="black" color={h.avg_delay_days > 0 ? 'red.500' : 'green.500'}>
                               {h.avg_delay_days === null || h.avg_delay_days === undefined ? '—' : h.avg_delay_days > 0 ? `+${h.avg_delay_days} days` : 'On Time'}{/* --- UPDATE: unknown is not "On Time" */}
                             </Text>
                           </VStack>
                         </SimpleGrid>
                         {/* --- UPDATE: why this grade + real lead time */}
                         {h.grade_reason && <Text fontSize="xs" color={muted} mb={3}>{h.grade_reason}{h.learned_lead_time_days != null ? ` Real lead time ${h.learned_lead_time_days} days (setting ${h.configured_lead_time_days ?? '—'}).` : ''}</Text>}

                         {isFailing && (
                           <Box bg={warnBg} p={4} borderRadius="xl" border="1px solid" borderColor="orange.200">{/* --- UPDATE: dark mode */}
                             <HStack justify="space-between">
                               <VStack align="start" spacing={0}>
                                 <HStack color={warnText}>
                                   <Icon as={MdWarning} />
                                   <Text fontSize="sm" fontWeight="bold">Performance Alert</Text>
                                 </HStack>
                                 <Text fontSize="xs" color="orange.600">Consistent delays are impacting operations.</Text>
                               </VStack>
                               <Button size="sm" colorScheme="orange" leftIcon={<Icon as={MdGavel} />} 
                                       onClick={() => handleDraftNegotiation(h)}>
                                 Renegotiate SLA
                               </Button>
                             </HStack>
                           </Box>
                         )}
                       </MotionBox>
                     );
                   })}
                 </SimpleGrid>
                 </>
               )}
             </MotionBox>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* ═══ MODALS ═══ */}
      {isModalOpen && (
        <SupplierFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          supplier={selectedSupplier}
          onSave={() => { setIsModalOpen(false); fetchSuppliers(true); }}
          existingNames={suppliers.filter((x) => !selectedSupplier || x.id !== selectedSupplier.id).map((x) => x.name)} // --- UPDATE: duplicate check
          score={selectedSupplier ? scoreById.get(String(selectedSupplier.id)) || null : null} // --- UPDATE: learned lead time
        />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay backdropFilter="blur(4px)">
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="800">
              <HStack>
                <Box bg="red.50" p={2} borderRadius="lg">
                  <DeleteIcon color="red.500" />
                </Box>
                <Text>Archive Supplier</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody color={muted} fontSize="sm">
              Archive <strong style={{ color: textColor }}>{deleteTarget?.name}</strong>? They will be removed
              from active procurement. This action can be reversed by your administrator.
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={onDeleteClose} borderRadius="xl" size="sm">Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete} isLoading={isDeleting}
                      loadingText="Archiving…" borderRadius="xl" size="sm">
                Archive Partner
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* AI Draft SLA Negotiation Modal */}
      <Modal isOpen={draftModalOpen} onClose={() => setDraftModalOpen(false)} size="2xl" isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader>
            <HStack color="purple.600">
              <Icon as={FaRobot} />
              <Text>AI Generated SLA Warning</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isDrafting ? (
              <Flex direction="column" align="center" justify="center" p={10}>
                <Spinner size="xl" color="purple.500" mb={4} />
                <Text fontWeight="bold" color={muted}>Analyzing vendor metrics...</Text>
                <Text fontSize="sm" color={muted}>Drafting formal remediation request.</Text>
              </Flex>
            ) : (
              <Box>
                <Alert status="warning" borderRadius="xl" mb={4}>
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Review and edit the AI-generated memo before sending to <strong>{activeHealthSupplier?.name}</strong>.
                  </AlertDescription>
                </Alert>
                <Textarea 
                  value={draftContent} 
                  onChange={(e) => setDraftContent(e.target.value)} 
                  rows={12} 
                  borderRadius="xl"
                  _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
                />
              </Box>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor={border}>
            <Button variant="ghost" onClick={() => setDraftModalOpen(false)} mr={3}>Cancel</Button>
            {/* --- UPDATE: copy, and Send opens the user's email program (nothing was sent before) */}
            <Button variant="outline" mr={3} leftIcon={<Icon as={MdContentCopy} />} isDisabled={isDrafting}
                    onClick={async () => { try { await navigator.clipboard.writeText(draftContent); toast({ title: 'Letter copied', status: 'success', duration: 1500 }); } catch (_) { toast({ title: 'Select the text to copy it', status: 'info' }); } }}>
              Copy
            </Button>
            <Button colorScheme="purple" leftIcon={<Icon as={EmailIcon} />} isDisabled={isDrafting}
                    onClick={() => {
                      const email = activeHealthSupplier?.email || suppliers.find((x) => String(x.id) === String(activeHealthSupplier?.supplier_id))?.email || '';
                      const [first, ...rest] = String(draftContent).split('\n');
                      const subject = first.replace(/^Subject:\s*/i, '');
                      window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(rest.join('\n').trim())}`;
                      toast({ title: email ? 'Opening your email program' : 'Opening your email program — add the supplier address', status: 'info', duration: 3000 });
                      setDraftModalOpen(false);
                    }}>
              Send to Vendor
            </Button>
            {/* --- END UPDATE */}
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default SuppliersTab;